import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import "./CustomerList.css";
import Loader from "./Loader";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const PAGE_SIZE = 5;

export default function CustomerList({ isModal = false }) {
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");

  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCustomers = async () => {
    try {
      setError("");
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/Customer`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      setCustomers(res.data?.data || []);
      setPage(1);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load customers.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return customers;

    return customers.filter((c) => {
      const name = (c?.name || "").toLowerCase();
      const contact = (c?.contactNumber || "").toString();
      return name.includes(q) || contact.includes(q);
    });
  }, [customers, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);

  const paginatedData = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, safePage]);

  useEffect(() => {
    if (page !== safePage) setPage(safePage);
  }, [page, safePage]);

  const exportExcel = async () => {
    try {
      setDownloading(true);
      await new Promise((r) => setTimeout(r, 80));

      const excelData = customers.map((c) => ({
        Name: c?.name ?? "",
        Contact: c?.contactNumber ?? "",
        Date: c?.date ?? "",
        Status: c?.isActive ? "Active" : "Inactive",
      }));

      const ws = XLSX.utils.json_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Customers");
      XLSX.writeFile(wb, "customers.xlsx");
    } catch (e) {
      console.error(e);
      alert("Excel download failed");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className={`cl-page ${isModal ? "cl-pageModal" : ""}`}>
      <Loader isLoading={downloading} message="Preparing Excel..." />

      <div className="cl-container">
        <div className="cl-head">
          <div className="cl-headLeft">
            <h2 className="cl-title">Customer Management</h2>
            <p className="cl-subtitle">
              Total: <b>{customers.length}</b> • Showing: <b>{filtered.length}</b>
            </p>
          </div>

          <div className="cl-headRight">
            <button className="cl-btn cl-btnGhost" onClick={fetchCustomers} type="button">
              Refresh
            </button>
          </div>
        </div>

        <div className="cl-toolbar">
          <div className="cl-search">
            <span className="cl-searchLeftIcon" aria-hidden="true">🔍</span>

            <input
              className="cl-searchInput"
              type="text"
              placeholder="Search name / number..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />

            <button
              type="button"
              className={`cl-searchClear ${search ? "isVisible" : ""}`}
              onClick={() => {
                setSearch("");
                setPage(1);
              }}
              aria-label="Clear search"
              title="Clear"
            >
              ×
            </button>
          </div>

          <div className="cl-actions">
            <button
              className="cl-btn cl-btnPrimary"
              onClick={exportExcel}
              type="button"
              disabled={downloading}
            >
              <span className="cl-btnIcon" aria-hidden="true">⬇</span>
              <span className="cl-btnText">Download Excel</span>
            </button>
          </div>
        </div>

        <div className="cl-content">
          {loading && <div className="cl-state">Loading...</div>}
          {!loading && error && <div className="cl-state cl-stateError">{error}</div>}

          {!loading && !error && filtered.length === 0 && (
            <div className="cl-empty">No customers found. Try another search.</div>
          )}

          {!loading && !error && filtered.length > 0 && (
            <>
              <div className="cl-tableWrap">
                <table className="cl-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Contact</th>
                      <th>Date</th>
                      <th>Status</th>
                    </tr>
                  </thead>

                  <tbody>
                    {paginatedData.map((c) => (
                      <tr key={c.id}>
                        <td className="cl-tdStrong">{c.name}</td>
                        <td>{c.contactNumber}</td>
                        <td className="cl-tdMuted">{c.date || "-"}</td>
                        <td>
                          <span className={`cl-pill ${c.isActive ? "isActive" : "isInactive"}`}>
                            {c.isActive ? "Active" : "Inactive"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="cl-cards">
                {paginatedData.map((c) => (
                  <div className="cl-card" key={c.id}>
                    <div className="cl-cardTop">
                      <div className="cl-cardName" title={c.name}>
                        {c.name}
                      </div>
                      <span className={`cl-pill ${c.isActive ? "isActive" : "isInactive"}`}>
                        {c.isActive ? "Active" : "Inactive"}
                      </span>
                    </div>

                    <div className="cl-kv">
                      <div className="cl-k">Contact</div>
                      <div className="cl-v">{c.contactNumber || "-"}</div>
                    </div>

                    <div className="cl-kv">
                      <div className="cl-k">Date</div>
                      <div className="cl-v">{c.date || "-"}</div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {!loading && !error && filtered.length > 0 && (
          <div className="cl-pagination">
            <button
              className="cl-btn cl-btnGhost"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={safePage === 1}
              type="button"
            >
              Prev
            </button>

            <div className="cl-pageInfo">
              Page <b>{safePage}</b> / <b>{totalPages}</b>
            </div>

            <button
              className="cl-btn cl-btnGhost"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage === totalPages}
              type="button"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}